/**
 * CANVAS STUDIO — the Videos tab's canvas mode (normal videos only — no avatar,
 * no talking heads: those are designed in the Talking Heads Studio).
 *
 * A video has two parts, and each gets its own canvas:
 *   • Narration canvas — the narration parts: the picture / footage under the
 *     voice sits in its box on this canvas.
 *   • Clip canvas — the [CLIP:…] parts: the clip sits in its box on this canvas.
 * Moving from one to the other plays the channel's transition banner.
 *
 * Both are sent with the render as `canvas: { enabled, narration_layout, clip_layout }`
 * and can be saved to a channel (and loaded back when that channel is picked).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaClone, FaImage, FaMagnet, FaPalette, FaRedo, FaSave, FaShapes, FaSlidersH, FaThLarge, FaUndo, FaLayerGroup,
} from 'react-icons/fa';
import { MdMovie, MdVideoLibrary } from 'react-icons/md';
import { toast } from 'react-toastify';
import api from '../../api';
import './th-studio.css';
import './canvas-studio.css';
import { getDefaultCompositionTemplate, saveCompositionTemplate } from './compositionTemplates';
import { useHistory } from './studioKit';
import ThStage from './ThStage';
import {
  BackgroundInspector, CanvasGallery, DecorInspector, DecorList, LayerInspector,
} from './ThInspector';
import {
  OUTPUT_PRESETS,
  buildFacelessPremade,
  buildLayoutConfig,
  designFromLayoutConfig,
  facelessDesign,
  reflowDesign,
} from './thCanvas';

const CANVAS_TYPE = 'canvas'; // channel canvas templates (backend type "canvas")

const SCENES = {
  narration: {
    label: 'Narration canvas',
    box: 'content',
    boxLabel: 'Narration visuals',
    hint: 'The picture or footage the voice talks over',
    icon: <MdVideoLibrary />,
    start: 'magazine',
  },
  clip: {
    label: 'Clip canvas',
    box: 'clip',
    boxLabel: 'Clip',
    hint: 'The real clips ([CLIP:…] parts)',
    icon: <MdMovie />,
    start: 'newsroom',
  },
};

const TABS = [
  { id: 'canvases', label: 'Canvases', icon: <FaThLarge /> },
  { id: 'edit', label: 'Edit', icon: <FaSlidersH /> },
  { id: 'background', label: 'Background', icon: <FaPalette /> },
  { id: 'decor', label: 'Decor', icon: <FaShapes /> },
  { id: 'channel', label: 'Channel', icon: <FaSave /> },
];

const headers = (user) => (user?.token ? { Authorization: `Bearer ${user.token}` } : {});

/** Each canvas belongs to one part, so its decorations always show there. */
function forScene(d, W, H) {
  const f = facelessDesign(d, W, H);
  return f.decor.some((l) => l.scope !== 'both')
    ? { ...f, decor: f.decor.map((l) => ({ ...l, scope: 'both' })) }
    : f;
}

function designFrom(cfg, preset) {
  const parsed = designFromLayoutConfig(cfg, preset);
  if (!parsed) return null;
  const p = OUTPUT_PRESETS[preset];
  const d = parsed.preset === preset ? parsed.design : reflowDesign(parsed.design, parsed.preset, preset);
  return forScene(d, p.w, p.h);
}

function startDesign(scene, preset) {
  const p = OUTPUT_PRESETS[preset];
  return forScene(buildFacelessPremade(SCENES[scene].start, p.w, p.h), p.w, p.h);
}

export default function CanvasStudio({
  user,
  orientation,
  configs,          // { narration: layout_config | null, clip: layout_config | null }
  onConfigChange,   // (scene, layout_config)
  channelId: initialChannelId,
}) {
  const preset = OUTPUT_PRESETS[orientation] ? orientation : 'landscape';
  const W = OUTPUT_PRESETS[preset].w;
  const H = OUTPUT_PRESETS[preset].h;

  const [scene, setScene] = useState('narration');
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const [design, setDesign] = useState(() => designFrom(configs?.narration, preset) || startDesign('narration', preset));
  const history = useHistory(design, setDesign);
  const [pristine, setPristine] = useState(!configs?.narration);

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('canvases');
  const [snap, setSnap] = useState(true);
  const [contentSample, setContentSample] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const sampleInputRef = useRef(null);
  const textRef = useRef(null);

  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(initialChannelId || '');
  const [channelTplId, setChannelTplId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingChannel, setLoadingChannel] = useState(false);

  // ── the canvas being edited → up to the page (sent with the render) ──
  const layoutConfig = useMemo(() => buildLayoutConfig(design, preset), [design, preset]);

  // The clip canvas FOLLOWS the narration canvas (the same design, the clip in the content box) until the
  // user makes it its own — edits it, picks a canvas for it, or loads a channel canvas with its own clip
  // design (owner, 2026-11-03: "no matter what I design, only one template works — the red border and white
  // border": the clip canvas started on Newsroom and stayed Newsroom while the narration canvas was designed,
  // so every [CLIP] part of the video came out red-framed with the white ticker). Kept in the config itself
  // (follows_narration) so it survives this panel closing and reopening, and channel saves.
  const clipFollowsRef = useRef(!configs?.clip || !!configs.clip.follows_narration);
  const [clipFollows, setClipFollows] = useState(clipFollowsRef.current);
  const setFollows = useCallback((v) => { clipFollowsRef.current = v; setClipFollows(v); }, []);
  const clipFromNarration = useCallback((narrCfg) => {
    const src = designFrom(narrCfg, preset);
    if (!src) return null;
    const d = forScene({ ...src, layers: { ...src.layers, clip: { ...src.layers.content } } }, W, H);
    return { ...buildLayoutConfig(d, preset), follows_narration: true };
  }, [preset, W, H]);

  useEffect(() => {
    if (scene === 'clip') {
      onConfigChange?.('clip', clipFollowsRef.current ? { ...layoutConfig, follows_narration: true } : layoutConfig);
      return;
    }
    onConfigChange?.('narration', layoutConfig);
    if (clipFollowsRef.current) {
      const c = clipFromNarration(layoutConfig);
      if (c) onConfigChange?.('clip', c);
    }
  }, [layoutConfig, scene, onConfigChange, clipFromNarration]);

  // the narration canvas also exists from the start (opened on the clip canvas first)
  useEffect(() => {
    if (!configs?.narration) onConfigChange?.('narration', buildLayoutConfig(startDesign('narration', preset), preset));
  }, [configs, preset, onConfigChange]);

  const update = useCallback((fn, opts) => {
    setPristine(false);
    if (sceneRef.current === 'clip') setFollows(false); // an edit makes the clip canvas its own
    history.set((d) => forScene(typeof fn === 'function' ? fn(d) : fn, W, H), opts);
  }, [history, W, H, setFollows]);

  const showDesign = useCallback((next, isPristine = false) => {
    history.set(next, { commit: true });
    setPristine(isPristine);
    setSelected(null);
  }, [history]);

  const switchScene = (s) => {
    if (s === scene) return;
    const next = designFrom(configs?.[s], preset) || startDesign(s, preset);
    sceneRef.current = s;
    setScene(s);
    history.reset();
    setDesign(next);
    setPristine(!configs?.[s]);
    setSelected(null);
    setTab('canvases');
  };

  /** Clip canvas = the narration canvas, the clip in the same box. */
  const copyNarration = () => {
    const src = designFrom(configs?.narration, preset);
    if (!src) return;
    const box = src.layers.content;
    setFollows(true); // it keeps matching the narration canvas from now on
    showDesign(forScene({ ...src, layers: { ...src.layers, clip: { ...box } } }, W, H));
    toast.success('Clip canvas now follows the narration canvas');
  };

  // ── channels ──
  useEffect(() => {
    let cancelled = false;
    api.get('/get-channels', { headers: headers(user) })
      .then((res) => { if (!cancelled) setChannels(res.data?.channels || []); })
      .catch(() => { if (!cancelled) setChannels([]); });
    return () => { cancelled = true; };
  }, [user]);

  const channelName = channels.find((c) => c._id === channelId)?.channelName || 'this channel';

  const loadChannelCanvas = useCallback(async (cid, { quiet = false } = {}) => {
    setChannelTplId(null);
    if (!cid) return;
    setLoadingChannel(true);
    try {
      const tpl = await getDefaultCompositionTemplate({ type: CANVAS_TYPE, channelId: cid }).catch(() => null);
      const pl = tpl?.payload || {};
      const got = {
        narration: pl.narration_layout || pl.layout_config || null,
        clip: pl.clip_layout || pl.layout_config || null,
      };
      if (!got.narration && !got.clip) {
        if (!quiet) toast.info('No canvas saved for this channel yet — design one and save it');
        return;
      }
      setChannelTplId(tpl._id || null);
      // one layout_config for both, or a clip canvas saved while following → it follows; else its own
      setFollows(!pl.clip_layout || !!pl.clip_layout.follows_narration);
      ['narration', 'clip'].forEach((s) => {
        if (got[s] && s !== sceneRef.current) onConfigChange?.(s, got[s]);
      });
      const mine = got[sceneRef.current] && designFrom(got[sceneRef.current], preset);
      if (mine) showDesign(mine);
      if (!quiet) toast.success(`Loaded ${channels.find((c) => c._id === cid)?.channelName || 'the channel'}'s canvas`);
    } finally {
      setLoadingChannel(false);
    }
  }, [onConfigChange, preset, showDesign, channels, setFollows]);

  const pickChannel = (cid) => {
    setChannelId(cid);
    loadChannelCanvas(cid);
  };

  // the channel already chosen for this video → its canvas (only if nothing was designed yet)
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current || !initialChannelId) return;
    autoLoadedRef.current = true;
    if (!configs?.narration && !configs?.clip) loadChannelCanvas(initialChannelId, { quiet: true });
  }, [initialChannelId, configs, loadChannelCanvas]);

  const saveToChannel = async () => {
    if (!channelId) { toast.error('Pick a channel first'); return; }
    const both = { ...configs, [scene]: layoutConfig };
    const missing = Object.entries(both).find(([, c]) => {
      const bg = c?.design?.background;
      return bg && ['image', 'video'].includes(bg.type) && !bg.url;
    });
    if (missing) {
      toast.error(`Upload the background ${SCENES[missing[0]].label.toLowerCase()} needs first`);
      return;
    }
    setSaving(true);
    try {
      const res = await saveCompositionTemplate({
        type: CANVAS_TYPE,
        name: `${channelName} canvas`,
        payload: { narration_layout: both.narration, clip_layout: both.clip },
        channelId,
        templateId: channelTplId || undefined,
        setAsDefault: true,
      });
      setChannelTplId(res?.template_id || channelTplId);
      toast.success(`Saved as ${channelName}'s canvas (narration + clip)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the canvas');
    } finally {
      setSaving(false);
    }
  };

  // ── canvas actions ──
  const applyPremade = useCallback((id) => {
    if (sceneRef.current === 'clip') setFollows(false); // a canvas picked for the clip parts is their own
    showDesign(forScene(buildFacelessPremade(id, W, H), W, H), true);
  }, [W, H, showDesign, setFollows]);

  const select = useCallback((sel) => {
    setSelected(sel);
    if (sel) setTab('edit');
  }, []);

  const handleBgUpload = async (file, type) => {
    setBgPreview(URL.createObjectURL(file));
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      const res = await api.post('/dreamface/upload-background', fd, {
        headers: { ...headers(user), 'Content-Type': 'multipart/form-data' },
      });
      update((d) => ({ ...d, background: { ...d.background, type, url: res.data.url } }), { commit: true });
      toast.success(`Background ${type} uploaded`);
    } catch {
      toast.error(`Failed to upload background ${type}`);
      setBgPreview(null);
    } finally {
      setBgUploading(false);
    }
  };

  const media = useMemo(() => ({
    avatarUrl: null, contentSample, clipSample: contentSample, bgPreview, onAvatarMeta: undefined,
  }), [contentSample, bgPreview]);

  const sc = SCENES[scene];
  const boxKey = sc.box;
  const box = design.layers[boxKey];
  const bgNeedsUpload = ['image', 'video'].includes(design.background.type) && !design.background.url;

  return (
    <div className="ths th-studio cvs" style={{ '--ths-ar': W / H }}>
      <header className="cvs-head">
        <div className="st-seg cvs-kind" role="radiogroup" aria-label="Which canvas">
          {Object.entries(SCENES).map(([k, v]) => (
            <button key={k} type="button" role="radio" aria-checked={scene === k} className={scene === k ? 'is-active' : ''} onClick={() => switchScene(k)}>
              {v.icon} <span>{v.label}</span>
            </button>
          ))}
        </div>
        <p className="cvs-head__note">
          {scene === 'narration'
            ? 'How the screen looks while the narrator talks over your pictures / footage.'
            : 'How the screen looks while a real clip plays.'}
          {' '}Your channel banner plays between the two.
          {scene === 'clip' && clipFollows && (
            <> <b>Following the narration canvas</b> — edit it or pick a canvas to give clips their own.</>
          )}
        </p>
        <div className="ths-undo" role="group" aria-label="History">
          <button type="button" className="st-icon-btn" onClick={history.undo} disabled={!history.canUndo} title="Undo" aria-label="Undo"><FaUndo /></button>
          <button type="button" className="st-icon-btn" onClick={history.redo} disabled={!history.canRedo} title="Redo" aria-label="Redo"><FaRedo /></button>
        </div>
      </header>

      <div className="ths-body cvs-body">
        <main className="ths-center">
          <div className="ths-toolbar">
            <span className="cvs-format">{sc.label} · {OUTPUT_PRESETS[preset].long}</span>
            <span className="ths-toolbar__spacer" />
            {scene === 'clip' && configs?.narration && (
              <button type="button" className="st-btn cvs-copy" onClick={copyNarration} title="Use the narration canvas for clips too">
                <FaClone /> Same as narration
              </button>
            )}
            <button type="button" className={`st-icon-btn ${snap ? 'is-active' : ''}`} onClick={() => setSnap((v) => !v)} title={snap ? 'Snapping on (hold Alt to skip)' : 'Snapping off'} aria-pressed={snap} aria-label="Snapping">
              <FaMagnet />
            </button>
            <button
              type="button"
              className={`st-icon-btn ${contentSample ? 'is-active' : ''}`}
              onClick={() => (contentSample ? setContentSample(null) : sampleInputRef.current?.click())}
              title={contentSample ? 'Remove preview sample' : 'Preview with a sample image (not uploaded)'}
              aria-label="Preview sample image"
            >
              <FaImage />
            </button>
            <input
              ref={sampleInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setContentSample(URL.createObjectURL(f)); }}
            />
          </div>

          <ThStage
            design={design}
            update={update}
            checkpoint={history.checkpoint}
            mode={scene}
            W={W}
            H={H}
            media={media}
            selected={selected}
            onSelect={select}
            snap={snap}
            onEditText={() => { setTab('edit'); setTimeout(() => textRef.current?.focus(), 60); }}
          />

          <div className="ths-layerbar" role="toolbar" aria-label="Boxes">
            {box && (
              <button
                type="button"
                className={`ths-layerchip ths-layerchip--${boxKey} ${selected?.kind === 'layer' ? 'is-active' : ''}`}
                onClick={() => select({ kind: 'layer', id: boxKey })}
              >
                {sc.icon}
                {sc.boxLabel}
                <em>{box.width}×{box.height}</em>
              </button>
            )}
            <span className="ths-layerbar__hint">{sc.hint}</span>
          </div>
        </main>

        <aside className="ths-inspector" aria-label="Inspector">
          <nav className="ths-tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'is-active' : ''} onClick={() => setTab(t.id)}>
                {t.icon}<span>{t.label}</span>
              </button>
            ))}
          </nav>
          <div className="ths-inspector__body">
            {tab === 'canvases' && (
              <CanvasGallery W={W} H={H} currentId={pristine ? design.canvasId : null} onApply={applyPremade} faceless previewMode={scene} />
            )}
            {tab === 'edit' && (
              selected?.kind === 'layer' ? (
                <LayerInspector design={design} layerKey={boxKey} W={W} H={H} update={update} mode={scene} avatarDims={null} onSelect={select} />
              ) : selected?.kind === 'decor' ? (
                <DecorInspector design={design} id={selected.id} update={update} W={W} H={H} textRef={textRef} onSelect={select} hideScope />
              ) : (
                <div className="ths-panel ths-pick">
                  <p className="ths-note">Select something on the canvas, or pick:</p>
                  <button type="button" className="ths-pick__item" disabled={!box} onClick={() => select({ kind: 'layer', id: boxKey })}>
                    <span className="ths-dot" style={{ background: boxKey === 'clip' ? '#60a5fa' : '#22c55e' }} />
                    <strong>{sc.boxLabel}</strong>
                    <em>{sc.hint}</em>
                  </button>
                  <button type="button" className="ths-pick__item" onClick={() => setTab('decor')}>
                    <FaLayerGroup className="ths-pick__icon" />
                    <strong>Decorations</strong>
                    <em>{design.decor.length} on this canvas</em>
                  </button>
                </div>
              )
            )}
            {tab === 'background' && (
              <BackgroundInspector design={design} update={update} onUpload={handleBgUpload} uploading={bgUploading} layoutKind={design.layoutKind} faceless />
            )}
            {tab === 'decor' && (
              <DecorList design={design} update={update} selectedId={selected?.kind === 'decor' ? selected.id : null} onSelect={select} H={H} W={W} />
            )}
            {tab === 'channel' && (
              <div className="ths-panel cvs-channel">
                <label className="cvs-field">
                  <span>Channel</span>
                  <select className="st-input" value={channelId} onChange={(e) => pickChannel(e.target.value)} disabled={loadingChannel}>
                    <option value="">Choose a channel…</option>
                    {channels.map((c) => <option key={c._id} value={c._id}>{c.channelName || 'Untitled channel'}</option>)}
                  </select>
                </label>
                <p className="st-hint">
                  Picking a channel loads its saved canvas. Saving stores <b>both</b> canvases
                  (narration and clip) as the channel’s canvas — its next videos start from it.
                </p>
                <button type="button" className="st-btn st-btn--primary" onClick={saveToChannel} disabled={!channelId || saving || bgUploading}>
                  <FaSave /> {saving ? 'Saving…' : `Save to ${channelId ? channelName : 'channel'}`}
                </button>
                {bgNeedsUpload && <p className="ths-warn">Upload the background {design.background.type} before saving.</p>}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
